/**
 * MONSTER TOWN — TAP ACKNOWLEDGEMENT. OWNED BY THE INTERIORS CREW.
 *
 * WHY THIS EXISTS. LAW #1 says everything you tap does something, and the
 * placeable audit proves that every def RETURNS a reaction. On screen it was
 * still possible to tap a thing and see nothing at all, for a reason no audit
 * can catch: the reaction does not happen until the monster has walked over and
 * arrived, and every prop's reaction is over in half a second. Tap the telly
 * from across the room and the honest experience is "I touched it and the game
 * ignored me" for as long as the walk takes.
 *
 * So the very first frame after a tap now shows a mark ON THE THING TAPPED: a
 * chunky ring around its feet and a bouncing arrow over its head. The mark says
 * "yes, that one, I am coming" for the whole walk, then pops when the reaction
 * fires. It is the one visible response that does not depend on what the def
 * declared, so a prop can never look dead.
 *
 * OLDER-IPAD RULES: one Group, built once, reused forever. Two draw calls, and
 * only while a mark is on screen. No allocation per tap, no new materials.
 */

import * as THREE from 'three'
import { bus } from '../core/events'
import { engine } from '../core/engine'
import { world } from '../core/scene'
import { hex } from '../core/palette'
import { cone, group } from '../render/kit'
import type { InstanceId, SceneId, Vec3 } from '../core/types'

/** How long the mark hangs around after the reaction fires, in seconds. */
const POP_SECONDS = 0.32
/** Safety valve: a mark never outlives this, whatever happens to the walk. */
const MAX_SECONDS = 12

type Phase = 'off' | 'waiting' | 'popping'

let root: THREE.Group | null = null
let ring: THREE.Object3D | null = null
let arrow: THREE.Object3D | null = null
let phase: Phase = 'off'
let t = 0
let baseScale = 1
/** Local-space height of the arrow for the CURRENT target. */
let arrowBaseY = 1.2
let target: InstanceId | null = null

/**
 * THREE materials for the whole game's tap marks, created once.
 *
 * They are NOT taken from `kit.toon()` on purpose: kit materials are shared and
 * cached across every prop that uses the same colour, and this mark fades itself
 * out by writing `opacity`. Doing that to a cached material would fade every
 * teal prop in the town along with it.
 */
const MARK_MATS: THREE.MeshBasicMaterial[] = []

function markMaterial(color: 'uiAccent' | 'uiPanel' | 'uiPrimary'): THREE.MeshBasicMaterial {
  const m = new THREE.MeshBasicMaterial({
    color: hex(color),
    transparent: true,
    depthWrite: false,
    // THE MARK IS CHROME, NOT SCENERY. Depth-tested, a ring on the floor sinks
    // under the floor plank it is standing on and the arrow disappears behind
    // the wardrobe it is pointing at — which is exactly the furniture a kid is
    // most likely to be confused about. It always draws on top, like the HUD.
    depthTest: false,
    toneMapped: false,
  })
  MARK_MATS.push(m)
  return m
}

function build(): THREE.Group {
  if (root !== null) return root
  const g = group('tapMark')

  // A flat ANNULUS on the floor, not a disc: it draws on top of everything (see
  // the material note), so a filled circle would hide the very thing it is
  // pointing at. Two thin rings, chunky ink-drawn look, four triangles each.
  const r = group('ring')
  const outer = new THREE.Mesh(new THREE.RingGeometry(0.6, 0.78, 24), markMaterial('uiAccent'))
  const inner = new THREE.Mesh(new THREE.RingGeometry(0.5, 0.58, 24), markMaterial('uiPanel'))
  for (const m of [outer, inner]) {
    m.rotation.x = -Math.PI / 2
    m.position.y = 0.06
  }
  r.add(outer, inner)
  g.add(r)
  ring = r

  // The arrow over the head. Points DOWN at the thing — a kid reads a pointing
  // finger long before they read anything else.
  const a = group('arrow')
  // Authored so the cone spans -0.31..+0.31 in `arrow`, i.e. centred on the
  // group's own origin. That makes the arrow's height one number to reason
  // about (`arrowBaseY`) instead of two, and its point sits just above the
  // thing rather than a metre over its head.
  const tip = cone(0.42, 0.62, { color: 'uiPrimary', y: -0.31, seg: 8 })
  tip.material = markMaterial('uiPrimary')
  tip.rotation.x = Math.PI
  a.add(tip)
  a.position.y = 1.5
  g.add(a)
  arrow = a

  for (const o of [outer, inner, tip]) {
    o.castShadow = false
    o.receiveShadow = false
    o.frustumCulled = false
    o.renderOrder = 900
  }
  root = g
  return g
}

function setMarkOpacity(v: number): void {
  for (const m of MARK_MATS) m.opacity = v
}

function hide(): void {
  phase = 'off'
  target = null
  if (root !== null) root.visible = false
  setMarkOpacity(1)
}

/** Park the mark in whichever scene is awake, at a size that suits its grid. */
function show(sceneId: SceneId, at: Vec3, tileSize: number, topY: number): void {
  const node = world.get(sceneId)
  if (node === undefined) return
  const g = build()
  if (g.parent !== node.actors) node.actors.add(g)
  baseScale = Math.max(0.75, tileSize * 0.95)
  g.position.set(at.x, at.y, at.z)
  g.scale.setScalar(baseScale)
  // Just above the thing itself, in the group's local (pre-scale) space.
  arrowBaseY = Math.max(0.75, topY / baseScale + 0.5)
  if (arrow !== null) arrow.position.y = arrowBaseY
  g.visible = true
  phase = 'waiting'
  t = 0
}

export function installTapMark(): void {
  bus.on('play:interactRequested', (e) => {
    const node = world.get(e.sceneId)
    if (node === undefined) return
    const p = node.grid.placement(e.instanceId)
    if (p === null) return
    const c = node.grid.footprintCenter(p.anchor, p.footprint, p.rot)
    target = e.instanceId
    // Sit the arrow above whatever was tapped, not at a fixed height: a rug and
    // a wardrobe both need the mark to read as pointing at THEM.
    const art = node.art.get(e.instanceId)
    let topY = node.grid.tileSize
    if (art !== undefined) {
      const box = new THREE.Box3().setFromObject(art)
      if (!box.isEmpty()) topY = Math.max(0.4, box.max.y - c.y)
    }
    show(e.sceneId, { x: c.x, y: node.grid.levelY(p.anchor.gy), z: c.z }, node.grid.tileSize, topY)
  })

  // The reaction landed: the mark bursts rather than blinking out.
  bus.on('play:interacted', (e) => {
    if (phase === 'off' || (target !== null && target !== e.instanceId)) return
    phase = 'popping'
    t = 0
  })

  // A kid who changes their mind and taps the floor gets their tap back.
  bus.on('play:walkTo', () => hide())
  bus.on('app:modeChanged', () => hide())
  bus.on('scene:left', () => hide())
  bus.on('build:removed', (e) => {
    if (target === e.instanceId) hide()
  })

  engine.addSystem({
    id: 'interactions.tapMark',
    order: 71,
    fixed: (dt) => {
      if (phase === 'off' || root === null) return
      t += dt
      if (phase === 'waiting') {
        if (t > MAX_SECONDS) {
          hide()
          return
        }
        // A slow breathing pulse while the monster walks over. Alive, not busy.
        const k = 1 + Math.sin(t * 6) * 0.07
        root.scale.setScalar(baseScale * k)
        // ABSOLUTE, never `+=`. Accumulating an offset every fixed step lets the
        // arrow drift off the top of the screen the longer a kid takes to walk.
        if (arrow !== null) arrow.position.y = arrowBaseY + Math.sin(t * 6) * 0.14
        if (ring !== null) ring.rotation.y = t * 1.1
        return
      }
      const u = Math.min(1, t / POP_SECONDS)
      root.scale.setScalar(baseScale * (1 + u * 0.85))
      setMarkOpacity(1 - u)
      if (u >= 1) {
        setMarkOpacity(1)
        hide()
      }
    },
  })
}

/** Harness/debug: is a tap mark on screen, and on what? */
export function tapMarkState(): { visible: boolean; phase: Phase; instanceId: string | null } {
  return {
    visible: root !== null && root.visible,
    phase,
    instanceId: target === null ? null : (target as string),
  }
}
